/**
 * Configuration for the AI Health Check wizard.
 *
 * Every code here must match what the clinical rule engine in
 * health_monitoring.py recognises. A typo does not fail loudly — it produces a
 * symptom the engine silently ignores, so a real danger sign would never raise
 * the urgency. Codes are grouped by the constant they mirror.
 */

export type MetricKey =
  | "heart_rate"
  | "respiratory_rate"
  | "systolic_bp"
  | "diastolic_bp"
  | "spo2"
  | "body_temperature"
  | "body_weight"
  | "body_height"
  | "blood_glucose"
  | "pain_score"
  | "sleep_duration";

type MetricSpec = {
  label: string;
  unit: string;
  /** Mirrors METRICS[...]["hard"] — the server rejects anything outside it. */
  hard: [number, number];
  placeholder: string;
  hint?: string;
  step?: string;
};

export const METRICS: Record<MetricKey, MetricSpec> = {
  heart_rate: { label: "Denyut jantung", unit: "bpm", hard: [25, 250], placeholder: "72" },
  respiratory_rate: {
    label: "Laju pernapasan",
    unit: "napas/menit",
    hard: [4, 80],
    placeholder: "16",
  },
  systolic_bp: { label: "Tekanan sistolik", unit: "mmHg", hard: [60, 260], placeholder: "120" },
  diastolic_bp: { label: "Tekanan diastolik", unit: "mmHg", hard: [30, 160], placeholder: "80" },
  spo2: { label: "Saturasi oksigen", unit: "%", hard: [50, 100], placeholder: "98" },
  body_temperature: {
    label: "Suhu tubuh",
    unit: "°C",
    hard: [30, 45],
    placeholder: "36.8",
    step: "0.1",
    hint: "Gunakan termometer. Webcam tidak dapat mengukur suhu.",
  },
  body_weight: {
    label: "Berat badan",
    unit: "kg",
    hard: [2, 400],
    placeholder: "65",
    step: "0.1",
  },
  body_height: { label: "Tinggi badan", unit: "cm", hard: [40, 250], placeholder: "170" },
  blood_glucose: {
    label: "Glukosa darah",
    unit: "mg/dL",
    hard: [20, 700],
    placeholder: "95",
    hint: "Catat apakah puasa atau setelah makan pada keterangan dokter.",
  },
  pain_score: {
    label: "Skala nyeri",
    unit: "/10",
    hard: [0, 10],
    placeholder: "0",
    hint: "0 = tidak nyeri, 10 = nyeri terberat.",
  },
  sleep_duration: {
    label: "Durasi tidur",
    unit: "jam",
    hard: [0, 24],
    placeholder: "7",
    step: "0.5",
  },
};

export type CheckType =
  | "quick"
  | "complete"
  | "hypertension"
  | "diabetes"
  | "respiratory"
  | "cardiac";

export const CHECK_PROGRAMS: {
  value: CheckType;
  label: string;
  description: string;
  metrics: MetricKey[];
}[] = [
  {
    value: "quick",
    label: "Cek Cepat",
    description: "Tanda vital dasar. Selesai dalam 2 menit.",
    metrics: ["heart_rate", "systolic_bp", "diastolic_bp", "body_temperature"],
  },
  {
    value: "complete",
    label: "Pemeriksaan Lengkap",
    description: "Seluruh tanda vital dan data antropometri.",
    metrics: [
      "heart_rate",
      "respiratory_rate",
      "systolic_bp",
      "diastolic_bp",
      "spo2",
      "body_temperature",
      "body_weight",
      "body_height",
      "blood_glucose",
    ],
  },
  {
    value: "hypertension",
    label: "Pantau Hipertensi",
    description: "Fokus pada tekanan darah dan denyut jantung.",
    metrics: ["systolic_bp", "diastolic_bp", "heart_rate", "body_weight"],
  },
  {
    value: "diabetes",
    label: "Pantau Diabetes",
    description: "Fokus pada glukosa darah dan berat badan.",
    metrics: ["blood_glucose", "body_weight", "body_height", "systolic_bp", "diastolic_bp"],
  },
  {
    value: "respiratory",
    label: "Pernapasan",
    description: "Saturasi oksigen dan laju napas.",
    metrics: ["spo2", "respiratory_rate", "heart_rate", "body_temperature"],
  },
  {
    value: "cardiac",
    label: "Jantung",
    description: "Denyut, tekanan darah, dan saturasi.",
    metrics: ["heart_rate", "systolic_bp", "diastolic_bp", "spo2"],
  },
];

/**
 * Mirrors RED_FLAG_CODES. Any of these ends the wizard immediately and sends
 * the patient to emergency services — no measurement can outweigh them.
 */
export const RED_FLAGS: { code: string; name: string; detail: string }[] = [
  {
    code: "severe_chest_pain",
    name: "Nyeri dada berat",
    detail: "Terasa menekan, menjalar ke lengan, rahang, atau punggung.",
  },
  {
    code: "severe_shortness_of_breath",
    name: "Sesak napas berat",
    detail: "Sulit bicara satu kalimat penuh atau sesak saat istirahat.",
  },
  {
    code: "unconscious",
    name: "Pingsan atau tidak sadar",
    detail: "Baru saja kehilangan kesadaran, meski sebentar.",
  },
  { code: "seizure", name: "Kejang", detail: "Kejang seluruh tubuh atau sebagian." },
  {
    code: "severe_bleeding",
    name: "Perdarahan hebat",
    detail: "Perdarahan yang tidak berhenti ditekan.",
  },
  {
    code: "confusion",
    name: "Bingung mendadak",
    detail: "Sulit mengenali tempat, waktu, atau orang.",
  },
  {
    code: "cyanosis",
    name: "Bibir atau ujung jari kebiruan",
    detail: "Warna kebiruan pada bibir, lidah, atau kuku.",
  },
];

/**
 * Non-emergency symptoms. `chest_pain` and `shortness_of_breath` are read by
 * the rule engine; the fever cluster feeds the fever-risk screening.
 */
export const SYMPTOMS: { code: string; name: string }[] = [
  { code: "chest_pain", name: "Nyeri dada ringan" },
  { code: "shortness_of_breath", name: "Sesak napas ringan" },
  { code: "fever", name: "Demam" },
  { code: "chills", name: "Menggigil" },
  { code: "sweating", name: "Berkeringat berlebih" },
  { code: "body_aches", name: "Nyeri otot atau badan" },
  { code: "fever_fatigue", name: "Lemas" },
  { code: "cough", name: "Batuk" },
  { code: "headache", name: "Sakit kepala" },
  { code: "dizziness", name: "Pusing berputar" },
  { code: "nausea", name: "Mual atau muntah" },
  { code: "palpitations", name: "Jantung berdebar" },
];

export const SEVERITIES = [
  { value: "mild", label: "Ringan" },
  { value: "moderate", label: "Sedang" },
  { value: "severe", label: "Berat" },
] as const;
